/**
 * Mixin methods that handle realtime chat actions
 */

import { mapGetters } from 'vuex'
const emoji = require('node-emoji')

export default {
  name: 'mixin-chat-mapping',
  computed: {
    ...mapGetters({
      contactsManagePersonal: 'permissions/contactsManagePersonal',
      contactsWrite: 'permissions/contactsWrite'
    }),
    disableReaction () {
      return this.selectedRoom?.roomType === 'd' && this.selectedRoom?.extension === null
    },
    firstMessage () {
      return this.messages[0]
    },
    lastMessage () {
      return this.messages[this.messages.length - 1]
    },
    // map rocket chat rooms to advanced chat object (npm)
    roomsFormatted () {
      const dmRooms = [...this.directConversations, ...this.smsConversations]
      const that = this
      const dmFormatted = dmRooms.map(function (room) {
        const usernames = room.usernames.map(function (username) {
          if (room.t === 'd') {
            if (that.currentUser?.username === username) {
              return {
                _id: username,
                username: that.currentUser?.name
              }
            } else {
              return {
                _id: username,
                username: room.displayUsername
              }
            }
          } else {
            return {
              _id: username,
              username: username
            }
          }
        })
        return {
          roomId: room.id,
          roomName: room.displayUsername,
          roomType: room.t,
          isSms: room.isSms,
          open: room.open,
          fromDid: room.isSms ? room.myConversationDid() : null,
          extension: room.to_user?.extension,
          avatar: room.to_user?.contact_image || room.to_user?.picture_profile,
          unreadCount: room?.unread,
          index: room.lastMessage?.ts,
          roomIcon: null,
          roomColor: null,
          ogRoom: room,
          archived: false,
          isMember: true,
          lastMessage: that.formatPreviewMessage(room.lastMessage),
          actions: room.isSms ? room.contextMenus(that.contactsWrite, that.contactsManagePersonal) : room.contextMenus(),
          users: usernames
        }
      })
      const channelsRooms = [...this.channels, ...this.groups]
      const channelsRoomsFormatted = channelsRooms.map(function (room) {
        const members = room.members?.map(function (member) {
          return {
            _id: member.username,
            username: member.name,
            avatar: '',
            status: {
              state: member.status,
              lastChanged: member._updatedAt
            }
          }
        })
        return {
          roomId: room.id,
          roomName: room.displayUsername,
          roomType: room.t,
          extension: null,
          avatar: null,
          ogRoom: room,
          open: room.open,
          isSms: false,
          fromDid: null,
          actions: room.contextMenus(that.currentUser),
          roomIcon: room.icon(that.currentUser),
          roomColor: room.iconColor(),
          unreadCount: room?.unread,
          index: room.lastMessage?.ts,
          archived: room.archived,
          isMember: room.isCurrentUserParticipating(that.currentUser),
          lastMessage: that.formatPreviewMessage(room.lastMessage),
          users: members
        }
      })
      return [...dmFormatted, ...channelsRoomsFormatted]
    },
    // return only open rooms
    roomsFiltered: function () {
      return this.roomsFormatted.filter(room => {
        if (this.singleRoom) {
          return true
        } else {
          return room.open
        }
      })
    }
  },
  methods: {
    formatMessage (roomMessage) {
      return {
        _id: roomMessage._id,
        content: roomMessage.msg,
        senderId: roomMessage.u.username,
        roomId: roomMessage.rid,
        username: roomMessage.u.name,
        avatar: this.getMessageAvatar(roomMessage),
        date: this.formatDate(roomMessage.ts?.$date || roomMessage.ts || (new Date())),
        timestamp: this.formatTime(roomMessage.ts?.$date || roomMessage.ts || (new Date())),
        system: false,
        saved: true,
        distributed: !roomMessage.msg.startsWith('❗ ['),
        seen: !roomMessage.msg.startsWith('❗ ['),
        deleted: false,
        failure: roomMessage.msg.startsWith('❗ ['),
        disableActions: this.disableReaction,
        disableReactions: this.disableReaction,
        reactions: this.formatReactions(roomMessage),
        files: this.formatAttachments(roomMessage),
        replyMessage: this.formatReplyMessage(roomMessage)
      }
    },

    formatReactions (roomMessage) {
      const formattedReactions = {}
      if (roomMessage?.reactions) {
        Object.keys(roomMessage.reactions).forEach(function (key) {
          formattedReactions[emoji.emojify(key)] = roomMessage.reactions[key].usernames
        })
      }
      return formattedReactions
    },

    formatPreviewMessage (roomMessage) {
      return {
        _id: roomMessage?._id,
        content: this.formatLastMessageContent(roomMessage),
        senderId: roomMessage?.u?.username,
        username: roomMessage?.u?.name,
        timestamp: this.formatMessageDatePreview((new Date(roomMessage?.ts))),
        saved: true,
        distributed: true,
        seen: true,
        new: true
      }
    },

    formatReplyMessage (roomMessage) {
      let replies = null
      if (roomMessage.tmid) {
        const relyMessage = this.messages.find(msg => msg._id === roomMessage.tmid)
        replies = {
          content: relyMessage?.content,
          senderId: relyMessage?.senderId,
          files: relyMessage?.files
        }
      }
      return replies
    },

    formatAttachments (roomMessage) {
      let files = []
      const that = this
      if (roomMessage.attachments?.length > 0) {
        let fileIndex = 0
        files = roomMessage.attachments.map(function (attachment) {
          const size = attachment?.image_size || attachment?.audio_size || attachment?.video_size
          const type = roomMessage?.files[fileIndex]?.type
          const urlPath = attachment?.title_link
          const url = `${that.chatUrl?.protocol}//${that.chatUrl?.host}${urlPath}`
          const previewUrl = attachment?.image_preview || attachment?.audio_preview || attachment?.video_preview
          fileIndex += 1
          const extension = type?.split('/')[1]
          const isMedia = that.isAudio(type) || that.isImage(type) || that.isVideo(type)
          const name = isMedia ? attachment?.title : `file.${extension}`
          return {
            name: name,
            extension: extension,
            size: size,
            type: type,
            audio: that.isAudio(type),
            image: that.isImage(type),
            video: that.isVideo(type),
            url: url,
            preview: previewUrl
          }
        })
      }
      return files
    },

    formatDate (msgDate) {
      const date = new Date(msgDate)
      const options = { weekday: 'short', month: 'short', day: '2-digit', year: 'numeric' }
      const formattedDate = date.toLocaleDateString(this.$i18n.locale, options)
      return formattedDate
    },

    formatTime (time) {
      return (new Date(time)).toLocaleTimeString(this.$i18n.locale)
    },

    formatLastMessageContent (roomMessage) {
      let message = roomMessage?.msg || ''
      if (roomMessage?.attachments?.length > 0) {
        const attachment = roomMessage?.attachments[0]
        const type = attachment?.image_type || attachment?.audio_type || attachment?.video_type
        if (this.isImage(type)) {
          message = `🖼️ ${message}`
        } else if (this.isAudio(type)) {
          message = `🔊 ${message}`
        } else if (this.isVideo(type)) {
          message = `🎥 ${message}`
        } else {
          message = `📎 ${message}`
        }
      }
      return emoji.emojify(message)
    },

    formatMessageDatePreview (date) {
      let localizedLastMessageDate
      const lastMessageDate = this.fromNow(date)
      if (lastMessageDate) {
        if (lastMessageDate.text === 'just now') {
          localizedLastMessageDate = this.$t('chat.date.now')
        } else {
          localizedLastMessageDate = this.$tc(`chat.date.${lastMessageDate.interval.unit}`, lastMessageDate.number)
        }
      } else {
        localizedLastMessageDate = this.$t('chat.date.now')
      }
      return localizedLastMessageDate
    }
  }
}
